import React, { createContext, useContext, useState } from "react";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { faBox } from "@fortawesome/pro-regular-svg-icons";

import { useEditFlow } from "src/api/queries";
import { Icon } from "src/design-system/Icon";
import { fixCursorSnapOffset } from "src/utils/fixCursorSnapCollisonOffset";
import { wrapWithAxiosResponseErrorHandler } from "src/utils/toastError";

export const DND_NO_FOLDER_ID = "dndNoFolder" as const;

const FlowDndAvailableContext = createContext(false);

export const useIsFlowDndAvailable = () => useContext(FlowDndAvailableContext);

const FlowDragOverlay: React.FC<{
  name: string | undefined;
}> = ({ name }) => {
  return (
    <div className="flex w-fit min-w-[180px] cursor-default items-center gap-2 rounded-lg bg-white p-4 font-inter-medium-13px text-gray-700 shadow-sm">
      <Icon icon={faBox} size="md" />
      {name}
    </div>
  );
};

type FlowDndProviderProps = {
  workspaceId: string;
  children: React.ReactNode;
};

export const FlowDndProvider: React.FC<FlowDndProviderProps> = ({
  workspaceId,
  children,
}) => {
  const editFlow = useEditFlow(workspaceId);
  const [dndActiveId, setDndActiveId] = useState<string | number>();
  const [dndActiveName, setDndActiveName] = useState<string>();

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 20,
    },
  });

  const onDragStart = (event: DragStartEvent) => {
    setDndActiveId(event.active.id);
    setDndActiveName(event.active.data.current?.name as string | undefined);
  };

  const onDragEnd = (event: DragEndEvent) => {
    setDndActiveId(undefined);
    setDndActiveName(undefined);
    const { active, over } = event;
    if (!over) return;
    if (typeof active.id !== "string" || typeof over.id !== "string") return;

    const oldFolderId =
      (active.data.current?.folderId as string | null) ?? null;
    const targetFolderId =
      (over.data.current?.folderId as string | null) ?? null;

    if (oldFolderId !== targetFolderId) {
      wrapWithAxiosResponseErrorHandler(editFlow.mutateAsync)({
        flowId: active.id,
        flow_folder_id: targetFolderId,
        old_folder_id: oldFolderId,
      });
    }
  };

  return (
    <FlowDndAvailableContext.Provider value={true}>
      <DndContext
        collisionDetection={fixCursorSnapOffset}
        sensors={[mouseSensor]}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
      >
        {children}
        <DragOverlay
          dropAnimation={null}
          modifiers={[snapCenterToCursor]}
          style={{ pointerEvents: "none" }}
        >
          {dndActiveId !== undefined && (
            <FlowDragOverlay name={dndActiveName} />
          )}
        </DragOverlay>
      </DndContext>
    </FlowDndAvailableContext.Provider>
  );
};
